/**
 * WorkPackagesForm - Step 3: Work Packages Definition
 */

import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Grid
} from '@mui/material';
import { AutoAwesome as AIIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { WorkPackage, BasicInfo, ProjectContent } from '../types';
import { API_URL } from '../config';

interface Props {
  data: WorkPackage[];
  onChange: (wps: WorkPackage[]) => void;
  basicInfo: BasicInfo | null;
  content: ProjectContent;
  projectId?: string;
}

const WorkPackagesForm: React.FC<Props> = ({ data, onChange, basicInfo, content, projectId }) => {
  const [workPackages, setWorkPackages] = React.useState<WorkPackage[]>(data || []);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);

  React.useEffect(() => {
    onChange(workPackages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workPackages]);

  const generateWorkPackages = async () => {
    if (!basicInfo) {
      setError('Please complete basic information first');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Call backend API to generate WPs with FULL project context
      const response = await fetch(`${API_URL}/api/ai/generate-work-packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: 'temp',
          objectives: [], // Will be filled from project content
          duration: basicInfo.duration,
          partners: basicInfo.partners,
          budget: basicInfo.budget,
          // NEW: Pass full Project Content for SPECIFIC WPs
          problem_statement: content.problem_statement || '',
          main_objective: content.main_objective || '',
          target_groups: content.target_groups || [],
          expected_results: content.expected_results || [],
          innovation_elements: content.innovation_elements || '',
          eu_priorities: content.eu_priorities || []
        })
      });

      if (!response.ok) throw new Error('Failed to generate work packages');

      const result = await response.json();
      setWorkPackages(result.work_packages);
    } catch (err: any) {
      setError(err.message || 'Failed to generate work packages');
    } finally {
      setIsGenerating(false);
    }
  };

  const saveWorkPackages = async () => {
    if (!projectId) {
      setError('No project ID available. Please generate the project first.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const response = await fetch(`${API_URL}/api/project/${projectId}/work-packages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workPackages)
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save work packages');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
        📦 Work Packages Structure
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'white !important' }}>
        Define the work packages or let AI generate them based on your objectives
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✅ Work Packages saved successfully!
        </Alert>
      )}

      {/* Save WPs Button */}
      {workPackages.length > 0 && projectId && (
        <Paper sx={{ p: 2, mb: 3, textAlign: 'center', bgcolor: 'success.light' }}>
          <Button
            variant="contained"
            color="success"
            onClick={saveWorkPackages}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : '💾 Save Work Packages to Project'}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Save your edited WPs before exporting the final document
          </Typography>
        </Paper>
      )}

      {/* AI Generation Option */}
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'primary.light' }}>
        <Typography variant="h6" gutterBottom>
          🤖 AI-Powered Work Package Generation
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Let AI automatically generate a complete Work Package structure based on your project objectives
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
          onClick={generateWorkPackages}
          disabled={isGenerating || !basicInfo}
        >
          {isGenerating ? 'Generating...' : 'Generate Work Packages with AI'}
        </Button>
      </Paper>

      {/* Display Generated WPs */}
      {workPackages.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Generated Work Packages ({workPackages.length})
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={generateWorkPackages}
              disabled={isGenerating}
              size="small"
            >
              Regenerate
            </Button>
          </Box>

          {workPackages.map((wp, wpIndex) => (
            <Paper key={wp.number} sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                WP{wp.number}
              </Typography>

              <Grid container spacing={2}>
                {/* Title */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={wp.title}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].title = e.target.value;
                      setWorkPackages(updated);
                    }}
                    variant="outlined"
                  />
                </Grid>

                {/* Leader, Duration, Budget */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Leader"
                    value={wp.leader}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].leader = e.target.value;
                      setWorkPackages(updated);
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Duration (e.g., M1-M12)"
                    value={`M${wp.duration.start}-M${wp.duration.end}`}
                    onChange={(e) => {
                      const match = e.target.value.match(/M?(\d+)-M?(\d+)/);
                      if (match) {
                        const updated = [...workPackages];
                        updated[wpIndex].duration = { start: parseInt(match[1]), end: parseInt(match[2]) };
                        setWorkPackages(updated);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Budget (€)"
                    type="number"
                    value={wp.budget}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].budget = parseInt(e.target.value);
                      setWorkPackages(updated);
                    }}
                  />
                </Grid>

                {/* Objective */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Objective"
                    value={wp.objective}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].objective = e.target.value;
                      setWorkPackages(updated);
                    }}
                  />
                </Grid>

                {/* Activities */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Activities (one per line)"
                    value={wp.activities.join('\n')}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].activities = e.target.value.split('\n').filter(a => a.trim());
                      setWorkPackages(updated);
                    }}
                    helperText="Enter each activity on a new line"
                  />
                </Grid>

                {/* Deliverables */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Deliverables (one per line)"
                    value={wp.deliverables.join('\n')}
                    onChange={(e) => {
                      const updated = [...workPackages];
                      updated[wpIndex].deliverables = e.target.value.split('\n').filter(d => d.trim());
                      setWorkPackages(updated);
                    }}
                    helperText="Enter each deliverable on a new line"
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}

          {/* Budget Summary */}
          <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              💰 Budget Distribution
            </Typography>
            <Typography variant="body2">
              Total Allocated: €{workPackages.reduce((sum, wp) => sum + wp.budget, 0).toLocaleString()} / €{basicInfo?.budget.toLocaleString()}
            </Typography>
          </Paper>
        </>
      )}

      {workPackages.length === 0 && !isGenerating && (
        <Alert severity="info">
          Click "Generate Work Packages with AI" to automatically create a complete WP structure
        </Alert>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
        <Typography variant="body2">
          💡 <strong>Standard Structure:</strong> WP1 (Management), WP2-6 (Core Activities), WP7 (Quality), WP8 (Dissemination)
        </Typography>
      </Box>
    </Box>
  );
};

export default WorkPackagesForm;

